import type { Printing } from "@webscan/catalogue.js";
import type { Candidate, Verdict } from "@webscan/decide.js";
import { potentialPrice } from "./prices";

/**
 * The price-tie policy: an ambiguity that does not matter financially is settled.
 *
 * Printings of one artwork tie on every retrieval and geometry signal by construction,
 * so the commonest AMBIGUOUS read is a handful of printings at exactly the same match
 * percentage, waiting for a set symbol or a collector number nobody has read. When those
 * tied printings are worth about the same, the review is spent deciding nothing: the
 * user asked for the more valuable one to be taken whenever the tied group lies within a
 * threshold of each other, and that is the whole of this rule.
 *
 * What it will not do: touch a read that is not ambiguous, settle a tie that includes a
 * *different card* (the printing is the open question, never the identity), guess at an
 * unpriced printing, compare across currencies, or move a threshold of zero. The engine's
 * verdict is never changed — the session row records that the price decided, the way it
 * records that the user did.
 */

/** How a candidate's match is displayed, and therefore what "the same match" means. */
export function matchPercent(score: number): number {
  return Math.round(score * 100);
}

export interface PriceTie {
  /** The most valuable of the tied printings — the leader when values tie too. */
  printing: Printing;
  /** The tied printings, best first. */
  group: Candidate[];
  percent: number;
  currency: "USD" | "EUR";
  /** Highest minus lowest potential price across the group, in cents. */
  spreadCents: number;
}

export function settleByPrice(
  identification: {
    verdict: Verdict;
    candidates: readonly Candidate[];
    printing: Printing | null;
  },
  thresholdCents: number,
): PriceTie | null {
  if (!(thresholdCents > 0)) return null;
  if (identification.verdict !== "AMBIGUOUS" || !identification.printing) return null;
  const { candidates } = identification;
  if (candidates.length < 2) return null;

  const leader = candidates[0];
  const percent = matchPercent(leader.score);
  const group = candidates.filter((candidate) => matchPercent(candidate.score) === percent);
  // A leader alone at its percentage is a margin problem, not a tie; the rule is for ties.
  if (group.length < 2) return null;
  // Same name or nothing: a tie between two cards is a doubt about what was scanned.
  if (group.some((candidate) => candidate.printing.name !== leader.printing.name)) return null;

  const prices = group.map((candidate) => potentialPrice(candidate.printing));
  const currency = prices[0].currency;
  if (prices.some((price) => price.cents === null || price.currency !== currency)) return null;
  const cents = prices.map((price) => price.cents as number);
  const spreadCents = Math.max(...cents) - Math.min(...cents);
  if (spreadCents > thresholdCents) return null;

  let best = 0;
  for (let i = 1; i < cents.length; i++) if (cents[i] > cents[best]) best = i;
  return { printing: group[best].printing, group, percent, currency, spreadCents };
}
